import { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate, useOutletContext } from 'react-router-dom';
import { useMMAuth } from '@machinemetrics/mm-react-tools';
import { listTemplates, generateScenario, dispatchScenario } from '../../services/planningApi';
import { getParts } from '../../services/erpApi';
import { sandboxCapacity, statusColorVar } from '../../utils/sandboxCapacity';
import { DEFAULT_COMPANY } from '../../constants/company';
import { TYPE_ORDER as GROUP_ORDER, TYPE_LABEL as GROUP_LABEL, TYPE_COLOR as GROUP_COLOR } from '../../constants/machineTypes';
import { fetchSaved, createSaved, deleteSaved, migrateLegacyLocal, demandOverrideFromSaved, policyFromSaved } from '../../utils/sandboxScenarios';

// ── Capacity Sandbox (Mode 1) ────────────────────────────────────────────────
// Design a weekly demand scenario and test whether the plant can support it. Demand mix + plant
// station counts are the levers; capacity meters fill live. Plant edits are HYPOTHETICAL (sizing
// math only — they never touch real machines). "Run in Flow Planner" carries the authored demand
// into Mode 2 to see the week actually unfold.

// GROUP_AB stays local: two-letter badge lettering is this page's own presentational choice, not
// part of the shared type vocabulary (order/label/colour, imported above).
const GROUP_AB = { SAND_CORE: 'SC', SAND_MOLD: 'SM', POUR: 'PR', LATHE: 'LA', MILL: 'MI', PRESS: 'PS', SWISS_LATHE: 'SW' };
const RL = 50; // redline at 50% of the meter track → track shows up to 200%

const fmt = (n) => Math.round(n || 0).toLocaleString('en-US');
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
const orderGroups = (keys) => [...GROUP_ORDER.filter((g) => keys.includes(g)), ...keys.filter((g) => !GROUP_ORDER.includes(g))];

// Always rendered inside the Plan shell, which owns the title/company — hence no own header.
export default function CapacitySandboxPage() {
  const [searchParams] = useSearchParams();
  const companySlug = searchParams.get('company') || DEFAULT_COMPANY;
  const navigate = useNavigate();
  const auth = useMMAuth();
  const accessToken = auth?.accessToken ?? null;
  const { setCollapsed } = useOutletContext() || {};

  // Fullscreen / focus mode: kiosk the cockpit and auto-collapse the sidebar.
  const [isFs, setIsFs] = useState(false);
  useEffect(() => {
    const onFs = () => { const fs = !!document.fullscreenElement; setIsFs(fs); if (setCollapsed) setCollapsed(fs); };
    document.addEventListener('fullscreenchange', onFs);
    return () => document.removeEventListener('fullscreenchange', onFs);
  }, [setCollapsed]);
  const toggleFs = () => {
    if (document.fullscreenElement) document.exitFullscreen?.();
    else document.documentElement.requestFullscreen?.();
  };

  // Reload persisted scenarios when the company changes. Server-backed (they were localStorage, so
  // a scenario tuned here was invisible on the booth tablet), and this is also where anything still
  // sitting in the old per-browser store gets pushed up — once per company, idempotent by name.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const rows = await fetchSaved(companySlug, accessToken);
        if (!alive) return;
        const moved = await migrateLegacyLocal(companySlug, accessToken, rows);
        if (!alive) return;
        setScenarios(moved > 0 ? await fetchSaved(companySlug, accessToken) : rows);
      } catch { /* an unreachable convenience store must not take the page down */ }
    })();
    return () => { alive = false; };
  }, [companySlug, accessToken]);

  const [template, setTemplate] = useState(null);
  const [parts, setParts]       = useState([]);          // only the scenario's parts, with operations
  const [demand, setDemand]     = useState({});          // partId → weeklyQty
  const [stations, setStations] = useState({});          // group → count (hypothetical)
  const [cycleMult, setCycleMult] = useState({});        // group → cycle-time multiplier (hypothetical; 1 = as-routed)
  const [realTime, setRealTime] = useState(false);       // run each op at its G-code's full compiled length (real-time)
  const [scenarios, setScenarios] = useState([]); // saved scenarios (server-backed; loaded below)
  const [activeScen, setActiveScen] = useState('baseline');
  const [showCompare, setShowCompare] = useState(false);
  const [loading, setLoading]   = useState(true);
  const [running, setRunning]   = useState(false);
  const [dispatching, setDispatching] = useState(false);
  const [error, setError]       = useState(null);

  // Flow policy — the transfer/lot-streaming levers this plan dispatches with. Default is the
  // engine's 25%-of-lot; per-edge overrides (absolute parts) let one edge stream differently from the
  // rest (SAND_MOLD limited by pallets, MILL streaming freely). Trigger is QUANTITY only for now.
  const [xferMode, setXferMode] = useState('TRANSFER_BATCH'); // BATCH | TRANSFER_BATCH | SINGLE_PIECE
  const [xferPct, setXferPct]   = useState(25);               // default size, % of lot (TRANSFER_BATCH)
  const [edgeBatch, setEdgeBatch] = useState({});             // machineType → absolute parts (per-edge override)
  const [castings, setCastings] = useState({});              // partId → castings on hand (starting WIP feeding the lathe)
  const [presetBatch, setPresetBatch] = useState({});        // machineType → tool-life preset units (override)

  // baseline from the template (part set + weekly quantities + plant roster)
  const baseline = useMemo(() => {
    const b = { demand: {}, stations: {}, partMeta: {} };
    for (const d of (template?.demandBaseline || [])) {
      b.demand[d.partId] = d.weeklyQty;
      b.partMeta[d.partId] = { partNumber: d.partNumber, weeklyQty: d.weeklyQty };
    }
    for (const r of (template?.machineRoster || [])) b.stations[r.resourceGroup] = r.count;
    return b;
  }, [template]);
  const baseTotal = useMemo(() => Object.values(baseline.demand).reduce((s, q) => s + q, 0), [baseline]);

  // As-routed cycle time per group (avg across parts) — the reference the cycle levers scale.
  const refCycle = useMemo(() => {
    const sum = {}, n = {};
    for (const p of parts) for (const op of (p.operations || [])) {
      sum[op.machineType] = (sum[op.machineType] || 0) + (op.cycleTime || 0);
      n[op.machineType] = (n[op.machineType] || 0) + 1;
    }
    return Object.fromEntries(Object.keys(sum).map((k) => [k, n[k] ? sum[k] / n[k] : 0]));
  }, [parts]);

  // Tool keys (and seeded presets) per stage, from the scenario's parts' routings — so the Tool-life
  // section can offer an input per individual tool. machineType → [{ toolKey, preset }] (deduped).
  const toolsByStage = useMemo(() => {
    const out = {};
    for (const p of parts) for (const op of (p.operations || [])) {
      for (const t of (op.tools || [])) {
        (out[op.machineType] ||= []);
        if (!out[op.machineType].some((x) => x.toolKey === t.toolKey)) {
          out[op.machineType].push({ toolKey: t.toolKey, preset: t.preset });
        }
      }
    }
    for (const k of Object.keys(out)) out[k].sort((a, b) => a.toolKey.localeCompare(b.toolKey));
    return out;
  }, [parts]);

  useEffect(() => {
    let alive = true;
    setLoading(true); setError(null);
    (async () => {
      try {
        const [templates, allParts] = await Promise.all([
          listTemplates(companySlug, accessToken),
          getParts(companySlug),
        ]);
        const tmpl = templates[0] || null;
        if (!tmpl) { if (alive) { setTemplate(null); setLoading(false); } return; }
        const partIds = new Set((tmpl.demandBaseline || []).map((d) => d.partId));
        const scoped = allParts.filter((p) => partIds.has(p.id));
        if (!alive) return;
        setTemplate(tmpl);
        setParts(scoped);
        setDemand(Object.fromEntries((tmpl.demandBaseline || []).map((d) => [d.partId, d.weeklyQty])));
        setStations(Object.fromEntries((tmpl.machineRoster || []).map((r) => [r.resourceGroup, r.count])));
        setCycleMult({});
        setActiveScen('baseline');
      } catch (e) {
        if (alive) setError(describeErr(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [companySlug, accessToken]);

  const cap = useMemo(() => sandboxCapacity({ parts, demand, stations, cycleMult }), [parts, demand, stations, cycleMult]);
  const total = cap.total;
  const masterPct = baseTotal > 0 ? clamp(Math.round((total / baseTotal) * 100), 10, 300) : 100;

  const dirty = () => setActiveScen('');
  const setPart = (id, qty) => { setDemand((d) => ({ ...d, [id]: Math.max(0, qty) })); dirty(); };
  const scaleAll = (pct) => {
    const f = pct / 100;
    setDemand(Object.fromEntries(Object.entries(baseline.demand).map(([id, q]) => [id, Math.round(q * f)])));
    dirty();
  };
  const stepStation = (g, d) => { setStations((s) => ({ ...s, [g]: clamp((s[g] || 0) + d, 1, 12) })); dirty(); };
  const setCycleSeconds = (g, secs) => { const ref = refCycle[g] || 1; setCycleMult((m) => ({ ...m, [g]: secs / ref })); dirty(); };

  // A saved scenario captures the WHOLE configuration, including the flow policy and starting
  // inventory — otherwise "a scenario with casting" would silently drop its casting on save/load, and
  // every saved scenario would share whatever's currently typed. loadSnapshot restores all of it (and
  // resets to defaults for the Baseline chip, whose snapshot carries none of these).
  const loadSnapshot = (snap, key) => {
    setDemand({ ...snap.demand });
    setStations({ ...snap.stations });
    setCycleMult({ ...(snap.cycleMult || {}) });
    setCastings({ ...(snap.castings || {}) });
    setPresetBatch({ ...(snap.presetOverrides || {}) });
    setXferMode(snap.policy?.xferMode || 'TRANSFER_BATCH');
    setXferPct(snap.policy?.xferPct ?? 25);
    setEdgeBatch({ ...(snap.policy?.edgeBatch || {}) });
    setRealTime(!!snap.policy?.realTime);
    setActiveScen(key);
  };
  // Per-stage cycle-time overrides in ABSOLUTE seconds, derived from the (ratio-based) cycleMult and
  // the reference cycle. Only edited stages are included; the rest keep their seeded per-part times.
  // Stored on the saved scenario so a saved dispatch doesn't need refCycle to reconstruct them.
  const cycleOverrides = () => {
    const out = {};
    for (const [g, m] of Object.entries(cycleMult)) {
      if (m && m !== 1 && refCycle[g]) out[g] = Math.max(10, Math.round(refCycle[g] * m));
    }
    return out;
  };
  // The current sandbox config as a persistable scenario object (sans id/name).
  const currentConfig = () => ({
    demand: { ...demand }, stations: { ...stations }, cycleMult: { ...cycleMult },
    castings: { ...castings }, cycleOverrides: cycleOverrides(), presetOverrides: { ...presetBatch },
    policy: { xferMode, xferPct, edgeBatch: { ...edgeBatch }, realTime },
  });
  const saveScenario = async () => {
    const name = window.prompt('Name this scenario', `Scenario ${scenarios.length + 1}`);
    if (name === null) return; // cancelled
    try {
      const row = await createSaved(companySlug, { name: name.trim() || `Scenario ${scenarios.length + 1}`, ...currentConfig() }, accessToken);
      setScenarios((list) => [...list, row]);
      setActiveScen(row.id);
    } catch (e) {
      setError(describeErr(e));
    }
  };
  const removeScenario = async (id) => {
    try {
      await deleteSaved(companySlug, id, accessToken);
      setScenarios((list) => list.filter((s) => s.id !== id));
      if (activeScen === id) setActiveScen('baseline');
    } catch (e) {
      setError(describeErr(e));
    }
  };

  // Assemble the server planning policy for the CURRENT sandbox state — same builder saved scenarios
  // use (policyFromSaved), so dispatching live and dispatching a saved scenario resolve identically.
  const buildPolicy = () => policyFromSaved(currentConfig());

  // Dispatch a specific saved scenario straight to the floor (generate → commit → planned mode).
  const dispatchSaved = async (scn) => {
    if (dispatching || running) return;
    setDispatching(true); setError(null);
    try {
      await dispatchScenario(template.id, {
        demandOverride: demandOverrideFromSaved(scn), company: companySlug, policy: policyFromSaved(scn),
      }, accessToken);
      const q = new URLSearchParams();
      if (companySlug !== 'default') q.set('company', companySlug);
      navigate(`/floor${q.toString() ? `?${q.toString()}` : ''}`);
    } catch (e) {
      setError(describeErr(e));
      setDispatching(false);
    }
  };

  // Open a saved scenario in the Flow planner (generate a server scenario+plan from its config, then
  // navigate — this is how a saved scenario "shows on Flow").
  const openSavedInFlow = async (scn) => {
    if (dispatching || running) return;
    setRunning(true); setError(null);
    try {
      const { scenarioId, planId } = await generateScenario(template.id, {
        demandOverride: demandOverrideFromSaved(scn), policy: policyFromSaved(scn),
      }, accessToken);
      const q = new URLSearchParams();
      if (companySlug !== 'default') q.set('company', companySlug);
      q.set('scenario', scenarioId); q.set('plan', planId);
      navigate(`/plan/flow?${q.toString()}`);
    } catch (e) {
      setError(describeErr(e));
      setRunning(false);
    }
  };

  const runInFlowPlanner = async () => {
    if (!template) return;
    setRunning(true); setError(null);
    try {
      const demandOverride = Object.entries(demand).map(([partId, quantity]) => ({ partId, quantity }));
      const { scenarioId, planId } = await generateScenario(template.id, { demandOverride, policy: buildPolicy() }, accessToken);
      const q = new URLSearchParams();
      if (companySlug !== 'default') q.set('company', companySlug);
      q.set('scenario', scenarioId); q.set('plan', planId);
      navigate(`/plan/flow?${q.toString()}`);
    } catch (e) {
      setError(describeErr(e));
      setRunning(false);
    }
  };

  // One-click: generate + auto-plan + commit + planned mode, then go to the Live Floor.
  const dispatchToFloor = async () => {
    if (!template) return;
    setDispatching(true); setError(null);
    try {
      const demandOverride = Object.entries(demand).map(([partId, quantity]) => ({ partId, quantity }));
      await dispatchScenario(template.id, { demandOverride, company: companySlug, policy: buildPolicy() }, accessToken);
      const q = new URLSearchParams();
      if (companySlug !== 'default') q.set('company', companySlug);
      navigate(`/floor${q.toString() ? `?${q.toString()}` : ''}`);
    } catch (e) {
      setError(describeErr(e));
      setDispatching(false);
    }
  };

  if (loading) return <Pad muted>Loading capacity model…</Pad>;
  if (error && !template) return <Pad danger>{error}</Pad>;
  if (!template) return <Pad muted>No scenario template for “{companySlug}”. The Capacity Sandbox needs a company with a planning template (e.g. Rassini).</Pad>;

  const groups = orderGroups(cap.rows.map((r) => r.key));
  const rowByKey = Object.fromEntries(cap.rows.map((r) => [r.key, r]));
  const sevColor = `var(${cap.severity.colorVar})`;
  const activeSaved = scenarios.find((s) => s.id === activeScen) || null;

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', padding: '18px 26px', gap: 12, overflow: 'hidden' }}>
      {/* Masthead — the three numbers and the verdict they produce, on ONE row.
          PROXIMITY: Demand / Max feasible / Gap are the inputs to Plant status, so they sit together
          instead of the numbers spanning full width with the verdict orphaned in the far corner.
          The metrics group LEFT because that is where reading starts and where the eye lands first.
          VON RESTORFF: exactly one element is allowed to shout — Gap, coloured by sign. Plant status
          restates the same fact in words for scanning, so it is sized down rather than competing.
          The old "RASSINI · MODE 1 OF 2" eyebrow is gone: the company is already in the sidebar and
          the top bar, and "Mode 1 of 2" was internal jargon that the Sandbox tab now expresses. The
          descriptive paragraph moved to the tab's tooltip (Cognitive Load). */}
      <div style={{ display: 'flex', alignItems: 'stretch', gap: 12, flexWrap: 'wrap', flexShrink: 0 }}>
        <div style={{ display: 'flex', gap: 1, background: 'rgba(168,144,96,0.1)', border: '1px solid rgba(168,144,96,0.16)', borderRadius: 12, overflow: 'hidden' }}>
          <SummaryCell k="Demand" v={fmt(total)} unit="/wk" />
          <SummaryCell k="Max feasible" v={fmt(cap.maxFeasible)} unit="/wk" />
          <SummaryCell k="Gap" v={`${cap.gap >= 0 ? '+' : '−'}${fmt(Math.abs(cap.gap))}`} unit="/wk" color={cap.gap >= 0 ? 'var(--cn-success)' : 'var(--cn-danger)'} />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '0 16px', borderRadius: 12, border: '1px solid rgba(168,144,96,0.16)', background: 'var(--cn-surface)' }}>
          <span style={{ width: 10, height: 10, borderRadius: '50%', background: sevColor, flexShrink: 0, boxShadow: `0 0 10px ${sevColor}` }} />
          <div>
            <div style={{ fontSize: 9.5, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--cn-dimmed)' }}>Plant status</div>
            <div style={{ fontSize: 13.5, fontWeight: 800, color: sevColor }}>
              {cap.feasible ? `${cap.severity.label} · ${Math.round((cap.worst?.util || 0) * 100)}% at peak`
                            : `${cap.severity.label} · ${(cap.worst?.util || 0).toFixed(1)}× at ${GROUP_LABEL[cap.worst?.key] || cap.worst?.key}`}
            </div>
          </div>
        </div>

        {/* Scenario controls + fullscreen ride the same row — all three are chrome, not content. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginLeft: 'auto' }}>
          <span style={{ fontSize: 10, letterSpacing: '0.13em', textTransform: 'uppercase', color: 'var(--cn-dimmed)' }}>Scenarios</span>
          <ScenChip label="Baseline" active={activeScen === 'baseline'} onClick={() => loadSnapshot({ demand: baseline.demand, stations: baseline.stations, cycleMult: {} }, 'baseline')} />
          {scenarios.map((s) => <ScenChip key={s.id} label={s.name} active={activeScen === s.id} onClick={() => loadSnapshot(s, s.id)} />)}
          <button onClick={saveScenario} style={chipStyle(false, true)}>＋ Save current</button>
          {scenarios.length > 0 && (
            <button onClick={() => setShowCompare((v) => !v)} style={chipStyle(showCompare, false)}>
              {showCompare ? 'Hide compare' : 'Compare'}
            </button>
          )}
          <button onClick={toggleFs} title="Fullscreen focus mode (also collapses the sidebar)"
            style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--cn-muted)', background: 'var(--cn-surface2)', border: '1px solid rgba(168,144,96,0.2)', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontFamily: 'inherit' }}>
            {isFs ? '⤡ Exit' : '⤢ Fullscreen'}
          </button>
        </div>
      </div>

      {/* Actions for the selected SAVED scenario — dispatch it to the floor, open it in Flow, or delete
          it. This is what makes a saved scenario first-class: it persists (localStorage) and can be
          dispatched or previewed straight from here, not just compared. */}
      {activeSaved && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, flexWrap: 'wrap', padding: '8px 12px', borderRadius: 10, border: '1px solid rgba(168,144,96,0.16)', background: 'var(--cn-surface)' }}>
          <span style={{ fontSize: 11.5, color: 'var(--cn-muted)' }}>
            <b style={{ color: 'var(--cn-text)' }}>{activeSaved.name}</b> — saved scenario
          </span>
          <span style={{ flex: 1 }} />
          <button onClick={() => dispatchSaved(activeSaved)} disabled={dispatching || running}
            title="Generate + commit this scenario and start the machines now"
            style={{ fontSize: 12, fontWeight: 700, color: (dispatching || running) ? 'var(--cn-dimmed)' : '#1a1206', background: (dispatching || running) ? 'var(--cn-surface2)' : 'var(--cn-gold)', border: 'none', borderRadius: 8, padding: '7px 13px', cursor: (dispatching || running) ? 'default' : 'pointer', fontFamily: 'inherit' }}>
            {dispatching ? 'Dispatching…' : '▶ Dispatch to floor'}
          </button>
          <button onClick={() => openSavedInFlow(activeSaved)} disabled={dispatching || running}
            style={{ fontSize: 12, fontWeight: 700, color: 'var(--cn-text)', background: 'var(--cn-surface2)', border: '1px solid rgba(168,144,96,0.28)', borderRadius: 8, padding: '7px 13px', cursor: (dispatching || running) ? 'default' : 'pointer', fontFamily: 'inherit' }}>
            {running ? 'Opening…' : '→ Open in Flow'}
          </button>
          <button onClick={() => removeScenario(activeSaved.id)} title="Delete this saved scenario"
            style={{ fontSize: 12, fontWeight: 700, color: 'var(--cn-danger)', background: 'transparent', border: '1px solid rgba(192,57,43,0.4)', borderRadius: 8, padding: '7px 11px', cursor: 'pointer', fontFamily: 'inherit' }}>
            Delete
          </button>
        </div>
      )}

      {showCompare && scenarios.length > 0 && (
        <CompareTable baseline={baseline} scenarios={scenarios} parts={parts} />
      )}

      {error && <div style={{ ...bannerStyle, flexShrink: 0 }}>{error}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(280px, 340px) 1fr', gap: 16, flex: 1, minHeight: 0 }}>
        {/* Demand controls */}
        <Card title="Demand this week" cap="What the market is asking for. Drag the master, or set the mix per part." style={{ minHeight: 0, overflowY: 'auto' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 6 }}>
            <span style={{ fontSize: 27, fontWeight: 800, color: 'var(--cn-text)', fontVariantNumeric: 'tabular-nums' }}>{fmt(total)}</span>
            <span style={{ fontSize: 11, color: 'var(--cn-dimmed)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>rotors / wk</span>
          </div>
          <input type="range" min={10} max={300} value={masterPct} onChange={(e) => scaleAll(Number(e.target.value))}
            style={{ width: '100%', margin: '8px 0 12px' }} aria-label="Master demand (% of baseline)" />
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
            {[['×0.25', 25], ['×0.5', 50], ['Baseline', 100], ['×1.5', 150], ['×2', 200]].map(([lbl, pct]) => (
              <button key={lbl} onClick={() => scaleAll(pct)} style={chipStyle(false, false)}>{lbl}</button>
            ))}
          </div>
          {parts.map((p) => {
            const meta = baseline.partMeta[p.id] || {};
            return (
              <div key={p.id} style={{ padding: '8px 0', borderTop: '1px solid rgba(168,144,96,0.08)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                  <span style={{ fontSize: 12, color: 'var(--cn-gold)', fontWeight: 600 }}>{meta.partNumber || p.id.slice(0, 8)}</span>
                  <span style={{ fontSize: 12, color: 'var(--cn-text)', fontVariantNumeric: 'tabular-nums' }}>{fmt(demand[p.id])}</span>
                </div>
                <input type="range" min={0} max={9000} step={10} value={demand[p.id] ?? 0}
                  onChange={(e) => setPart(p.id, Number(e.target.value))} style={{ width: '100%' }}
                  aria-label={`${meta.partNumber} weekly demand`} />
              </div>
            );
          })}
        </Card>

        {/* Capacity + plant */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minHeight: 0, overflowY: 'auto' }}>
          <Card title="Capacity fill" cap="Required work vs. what the stations run in a 24/7 week (line = 100%). Drag Cycle (down to 10s) to set a faster run time per operation — cycle edits apply on dispatch, so the floor actually runs at that rate.">
            {/* Real-time: run each op at its G-code's FULL compiled length so the FOCAS block/line sweep
                smoothly instead of jumping. Overrides the per-stage Cycle sliders for machines with a
                program (lathes/mills); foundry stages keep their slider value. */}
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '2px 0 6px', cursor: 'pointer' }}>
              <input type="checkbox" checked={realTime} onChange={(e) => { setRealTime(e.target.checked); dirty(); }} />
              <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--cn-text)' }}>Real-time cycle</span>
              <span style={{ fontSize: 11, color: 'var(--cn-dimmed)' }}>match full program length — smooth block/line on FOCAS</span>
            </label>
            {groups.map((g) => {
              const r = rowByKey[g];
              const col = `var(${statusColorVar(r.util)})`;
              const over = r.util > 1;
              const refC = refCycle[g] || 0;
              const effCyc = Math.round(refC * (cycleMult[g] ?? 1));
              return (
                <div key={g} style={{ padding: '12px 0', borderTop: '1px solid rgba(168,144,96,0.08)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                    <span style={{ width: 22, height: 22, borderRadius: 6, background: GROUP_COLOR[g] || 'var(--cn-muted)', display: 'grid', placeItems: 'center', fontSize: 11, fontWeight: 800, color: '#0b1622' }}>{GROUP_AB[g] || g.slice(0, 2)}</span>
                    <span style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--cn-text)' }}>{GROUP_LABEL[g] || g}</span>
                    {over && r === cap.worst && <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--cn-danger)', border: '1px solid var(--cn-danger)', borderRadius: 4, padding: '1px 6px', marginLeft: 4 }}>bottleneck</span>}
                    <span style={{ marginLeft: 'auto', fontWeight: 700, fontSize: 17, color: col, fontVariantNumeric: 'tabular-nums' }}>
                      {over ? `${r.util.toFixed(1)}×` : `${Math.round(r.util * 100)}%`}
                    </span>
                  </div>
                  <div style={{ position: 'relative', height: 18, borderRadius: 5, background: 'rgba(255,255,255,0.05)', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.05)' }}>
                    <div style={{ position: 'absolute', top: 0, bottom: 0, left: 0, width: `${clamp(r.util, 0, 1) * RL}%`, background: col, transition: 'width 0.28s' }} />
                    {over && (
                      <div style={{ position: 'absolute', top: 0, bottom: 0, left: `${RL}%`, width: `${(clamp(r.util, 1, 2) - 1) * RL}%`, background: 'var(--cn-danger)', backgroundImage: 'repeating-linear-gradient(45deg, rgba(0,0,0,0.32) 0 6px, transparent 6px 12px)', transition: 'width 0.28s' }} />
                    )}
                    <div style={{ position: 'absolute', top: -3, bottom: -3, left: `${RL}%`, width: 2, background: '#fff', opacity: 0.5 }} />
                  </div>
                  {/* cycle-time lever — the hidden assumption behind the fill */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 7 }}>
                    <span style={{ fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--cn-dimmed)', minWidth: 38 }}>Cycle</span>
                    <input type="range" min={10} max={Math.max(20, Math.round(refC * 1.5))} value={effCyc} disabled={realTime}
                      onChange={(e) => setCycleSeconds(g, Number(e.target.value))} style={{ flex: 1, opacity: realTime ? 0.4 : 1 }} aria-label={`${GROUP_LABEL[g] || g} cycle time (seconds per piece)`} />
                    <span style={{ fontSize: 11.5, fontWeight: 700, color: realTime ? 'var(--cn-dimmed)' : ((cycleMult[g] ?? 1) !== 1 ? 'var(--cn-gold)' : 'var(--cn-text)'), fontVariantNumeric: 'tabular-nums', minWidth: 54, textAlign: 'right' }}>{realTime ? 'program' : `${effCyc}s/pc`}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 5, fontSize: 10.5, color: 'var(--cn-muted)', fontVariantNumeric: 'tabular-nums' }}>
                    <span>need {fmt(r.reqSec / 3600)}h</span>
                    <span>{r.count} station{r.count > 1 ? 's' : ''} · {fmt(r.availSec / 3600)}h</span>
                  </div>
                </div>
              );
            })}
          </Card>

          <Card title="Plant & sizing" cap="Add or remove stations (hypothetical — sizing only). Each row shows what you have vs. what this scenario needs.">
            {groups.map((g) => {
              const r = rowByKey[g];
              const deltaEl = r.delta > 0
                ? <b style={{ color: 'var(--cn-danger)' }}>+{r.delta}</b>
                : r.delta < 0 ? <b style={{ color: 'var(--cn-success)' }}>{r.delta}</b>
                : <b style={{ color: 'var(--cn-success)' }}>ok</b>;
              return (
                <div key={g} style={{ display: 'grid', gridTemplateColumns: '22px 1fr auto', alignItems: 'center', gap: 10, padding: '9px 0', borderTop: '1px solid rgba(168,144,96,0.08)' }}>
                  <span style={{ width: 22, height: 22, borderRadius: 6, background: GROUP_COLOR[g] || 'var(--cn-muted)', display: 'grid', placeItems: 'center', fontSize: 11, fontWeight: 800, color: '#0b1622' }}>{GROUP_AB[g] || g.slice(0, 2)}</span>
                  <div>
                    <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--cn-text)' }}>{GROUP_LABEL[g] || g}</div>
                    <div style={{ fontSize: 10.5, color: 'var(--cn-muted)', fontVariantNumeric: 'tabular-nums' }}>have {r.count} · need <b style={{ color: 'var(--cn-text)' }}>{r.requiredStations}</b> &nbsp;{deltaEl}</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <StepBtn onClick={() => stepStation(g, -1)} label={`remove ${GROUP_LABEL[g]}`}>–</StepBtn>
                    <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--cn-text)', minWidth: 16, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>{r.count}</span>
                    <StepBtn onClick={() => stepStation(g, 1)} label={`add ${GROUP_LABEL[g]}`}>+</StepBtn>
                  </div>
                </div>
              );
            })}
            <div style={{ marginTop: 14, padding: '13px 15px', borderRadius: 11, border: '1px solid rgba(168,144,96,0.16)', background: 'rgba(201,162,75,0.06)', fontSize: 13, lineHeight: 1.5, color: 'var(--cn-muted)' }}>
              {cap.feasible ? (
                <><b style={{ color: 'var(--cn-text)' }}>{GROUP_LABEL[cap.worst?.key]}</b> is the tightest link at {Math.round((cap.worst?.util || 0) * 100)}%. This plant can take up to <b style={{ color: 'var(--cn-text)' }}>{fmt(cap.maxFeasible)}</b> rotors/wk before it redlines.</>
              ) : (
                <><b style={{ color: 'var(--cn-text)' }}>{GROUP_LABEL[cap.worst?.key]}</b> is the bottleneck at {(cap.worst?.util || 0).toFixed(1)}×. To serve <b style={{ color: 'var(--cn-text)' }}>{fmt(total)}</b>/wk you'd need <b style={{ color: 'var(--cn-text)' }}>{cap.worst?.requiredStations}</b> {GROUP_LABEL[cap.worst?.key]} stations (have {cap.worst?.count}) — or cut demand to <b style={{ color: 'var(--cn-text)' }}>{fmt(cap.maxFeasible)}</b>/wk.</>
              )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8, marginTop: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <button onClick={runInFlowPlanner} disabled={running || dispatching}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 9, fontSize: 13, fontWeight: 700, color: (running || dispatching) ? 'var(--cn-dimmed)' : 'var(--cn-text)', background: 'var(--cn-surface2)', border: '1px solid rgba(168,144,96,0.28)', borderRadius: 10, padding: '11px 18px', cursor: (running || dispatching) ? 'default' : 'pointer', fontFamily: 'inherit' }}>
                  {running ? 'Generating…' : 'Run in Flow Planner →'}
                </button>
                <button onClick={dispatchToFloor} disabled={running || dispatching}
                  title="Generate + commit this scenario and put the plant to work now"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 9, fontSize: 13, fontWeight: 700, color: (running || dispatching) ? 'var(--cn-dimmed)' : '#1a1206', background: (running || dispatching) ? 'var(--cn-surface2)' : 'var(--cn-gold)', border: 'none', borderRadius: 10, padding: '11px 18px', cursor: (running || dispatching) ? 'default' : 'pointer', fontFamily: 'inherit' }}>
                  {dispatching ? 'Dispatching…' : '▶ Dispatch to floor'}
                </button>
              </div>
              <div style={{ fontSize: 10.5, color: 'var(--cn-dimmed)', textAlign: 'right', maxWidth: 340, lineHeight: 1.45 }}>
                <b style={{ color: 'var(--cn-muted)' }}>Flow Planner</b> previews the week — nothing is committed.{' '}
                <b style={{ color: 'var(--cn-muted)' }}>Dispatch</b> commits work orders and starts the machines now.
              </div>
            </div>
          </Card>

          <Card title="Flow policy" cap="How parts stream between operations within a work order. The default applies to every edge; override an edge to model a real constraint (pallets, furnace loads). Applies on dispatch.">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 4 }}>
              <label style={fpLabel}>Mode</label>
              <select value={xferMode} onChange={(e) => setXferMode(e.target.value)} style={fpSelect}>
                <option value="TRANSFER_BATCH">Transfer batch (stream)</option>
                <option value="BATCH">Batch (wait for full lot)</option>
                <option value="SINGLE_PIECE">Single piece</option>
              </select>
              {xferMode === 'TRANSFER_BATCH' && (
                <>
                  <label style={fpLabel}>Default</label>
                  <input type="number" min={1} max={100} value={xferPct} onChange={(e) => setXferPct(e.target.value)} style={fpInput} />
                  <span style={{ fontSize: 12, color: 'var(--cn-muted)' }}>% of lot</span>
                </>
              )}
            </div>

            {xferMode === 'TRANSFER_BATCH' && groups.length > 1 && (
              <div style={{ borderTop: '1px solid rgba(168,144,96,0.08)', marginTop: 12, paddingTop: 10 }}>
                <div style={{ fontSize: 11, color: 'var(--cn-dimmed)', marginBottom: 8 }}>
                  Per-edge override — parts that must accumulate before that stage pulls a lot. Blank = default. (The first stage has no upstream.)
                </div>
                {groups.slice(1).map((g) => (
                  <div key={g} style={{ display: 'grid', gridTemplateColumns: '22px 1fr 96px', alignItems: 'center', gap: 10, padding: '6px 0' }}>
                    <span style={{ width: 22, height: 22, borderRadius: 6, background: GROUP_COLOR[g] || 'var(--cn-muted)', display: 'grid', placeItems: 'center', fontSize: 11, fontWeight: 800, color: '#0b1622' }}>{GROUP_AB[g] || g.slice(0, 2)}</span>
                    <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--cn-text)' }}>→ {GROUP_LABEL[g] || g}</div>
                    <input type="number" min={1} placeholder="default" value={edgeBatch[g] ?? ''}
                      onChange={(e) => setEdgeBatch((m) => ({ ...m, [g]: e.target.value }))} style={fpInput} />
                  </div>
                ))}
              </div>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 12, borderTop: '1px solid rgba(168,144,96,0.08)', paddingTop: 12 }}>
              <label style={fpLabel}>Trigger</label>
              <select value="QUANTITY" disabled style={{ ...fpSelect, opacity: 0.85, cursor: 'not-allowed' }}>
                <option value="QUANTITY">Quantity (parts ready)</option>
              </select>
              <span style={{ fontSize: 11, color: 'var(--cn-dimmed)' }}>Time · Cooldown · Container — coming soon</span>
            </div>

            <div style={{ borderTop: '1px solid rgba(168,144,96,0.08)', marginTop: 12, paddingTop: 12 }}>
              <div style={{ fontSize: 11, color: 'var(--cn-dimmed)', marginBottom: 8 }}>
                Starting inventory — castings on hand per part, fed to the first machining stage so the
                lathes/mills run from t=0 instead of waiting on the sand line. Blank = start cold.
              </div>
              {Object.keys(demand).map((pid) => (
                <div key={pid} style={{ display: 'grid', gridTemplateColumns: '1fr 96px', alignItems: 'center', gap: 10, padding: '6px 0' }}>
                  <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--cn-text)' }}>{baseline.partMeta[pid]?.partNumber || pid}</div>
                  <input type="number" min={0} placeholder="0 castings" value={castings[pid] ?? ''}
                    onChange={(e) => setCastings((m) => ({ ...m, [pid]: e.target.value }))} style={fpInput} />
                </div>
              ))}
            </div>

            <div style={{ borderTop: '1px solid rgba(168,144,96,0.08)', marginTop: 12, paddingTop: 12 }}>
              <div style={{ fontSize: 11, color: 'var(--cn-dimmed)', marginBottom: 8 }}>
                Tool life — units before each tool needs changing, per individual tool. Lower = tool
                changes fire sooner (good for a demo). Blank = the part's seeded preset. Applies on dispatch.
              </div>
              {groups.filter((g) => (toolsByStage[g] || []).length).map((g) => (
                <div key={g} style={{ padding: '4px 0' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4, marginBottom: 2 }}>
                    <span style={{ width: 22, height: 22, borderRadius: 6, background: GROUP_COLOR[g] || 'var(--cn-muted)', display: 'grid', placeItems: 'center', fontSize: 11, fontWeight: 800, color: '#0b1622' }}>{GROUP_AB[g] || g.slice(0, 2)}</span>
                    <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--cn-text)' }}>{GROUP_LABEL[g] || g}</div>
                  </div>
                  {(toolsByStage[g] || []).map((t) => (
                    <div key={t.toolKey} style={{ display: 'grid', gridTemplateColumns: '22px 1fr 96px', alignItems: 'center', gap: 10, padding: '4px 0' }}>
                      <span />
                      <div style={{ fontSize: 12, color: 'var(--cn-muted)' }}>{t.toolKey}</div>
                      <input type="number" min={1} placeholder={`seeded ${t.preset}`} value={presetBatch[g]?.[t.toolKey] ?? ''}
                        onChange={(e) => setPresetBatch((m) => ({ ...m, [g]: { ...(m[g] || {}), [t.toolKey]: e.target.value } }))} style={fpInput} />
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>

      <div style={{ flexShrink: 0, fontSize: 11, color: 'var(--cn-dimmed)', textAlign: 'center' }}>
        Live capacity = Σ(qty × cycle) ÷ (stations × 604,800 s/wk) · station edits are sizing-only · cycle edits (down to 10s) apply on dispatch · setups omitted
      </div>
    </div>
  );
}

// ── Compare table ─────────────────────────────────────────────────────────────
function CompareTable({ baseline, scenarios, parts }) {
  const rows = [{ name: 'Baseline', demand: baseline.demand, stations: baseline.stations, cycleMult: {} }, ...scenarios];
  return (
    <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '32vh', flexShrink: 0, background: 'var(--cn-surface)', border: '1px solid rgba(168,144,96,0.12)', borderRadius: 12, padding: '14px 16px' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5, minWidth: 560 }}>
        <thead>
          <tr style={{ color: 'var(--cn-dimmed)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.07em' }}>
            {['Scenario', 'Demand', 'Bottleneck', 'Max feasible', 'Status'].map((h, i) => (
              <th key={h} style={{ textAlign: i === 0 ? 'left' : 'right', padding: '6px 10px', fontWeight: 700 }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((s) => {
            const c = sandboxCapacity({ parts, demand: s.demand, stations: s.stations, cycleMult: s.cycleMult || {} });
            const sc = `var(${c.severity.colorVar})`;
            return (
              <tr key={s.name} style={{ borderTop: '1px solid rgba(168,144,96,0.08)' }}>
                <td style={{ padding: '8px 10px', color: 'var(--cn-text)', fontWeight: 600 }}>{s.name}</td>
                <td style={{ padding: '8px 10px', textAlign: 'right', color: 'var(--cn-muted)', fontVariantNumeric: 'tabular-nums' }}>{fmt(c.total)}</td>
                <td style={{ padding: '8px 10px', textAlign: 'right', color: 'var(--cn-muted)' }}>{GROUP_LABEL[c.worst?.key] || '—'} · {(c.worst?.util || 0) > 1 ? `${(c.worst?.util).toFixed(1)}×` : `${Math.round((c.worst?.util || 0) * 100)}%`}</td>
                <td style={{ padding: '8px 10px', textAlign: 'right', color: 'var(--cn-muted)', fontVariantNumeric: 'tabular-nums' }}>{fmt(c.maxFeasible)}</td>
                <td style={{ padding: '8px 10px', textAlign: 'right', color: sc, fontWeight: 700 }}>{c.severity.label}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── Small building blocks ─────────────────────────────────────────────────────
function Card({ title, cap, children, style }) {
  return (
    <section style={{ background: 'var(--cn-surface)', border: '1px solid rgba(168,144,96,0.12)', borderRadius: 14, padding: 18, ...style }}>
      <h2 style={{ margin: '0 0 3px', fontSize: 13, fontWeight: 800, color: 'var(--cn-text)' }}>{title}</h2>
      <div style={{ fontSize: 11.5, color: 'var(--cn-dimmed)', marginBottom: 15 }}>{cap}</div>
      {children}
    </section>
  );
}
function SummaryCell({ k, v, unit, color }) {
  return (
    // minWidth keeps the three cells legible now that they sit in a left-grouped flex row rather
    // than a full-width 3-column grid — without it the labels wrap at narrow widths.
    <div style={{ background: 'var(--cn-surface)', padding: '11px 18px', minWidth: 132 }}>
      <div style={{ fontSize: 10, letterSpacing: '0.13em', textTransform: 'uppercase', color: 'var(--cn-dimmed)', marginBottom: 4, whiteSpace: 'nowrap' }}>{k}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: color || 'var(--cn-text)', fontVariantNumeric: 'tabular-nums' }}>
        {v}<span style={{ fontSize: 11, color: 'var(--cn-muted)', marginLeft: 5, fontWeight: 400 }}>{unit}</span>
      </div>
    </div>
  );
}
const StepBtn = ({ onClick, label, children }) => (
  <button onClick={onClick} aria-label={label} style={{ width: 26, height: 26, borderRadius: 7, border: '1px solid rgba(168,144,96,0.2)', background: 'var(--cn-surface2)', color: 'var(--cn-text)', fontSize: 16, fontWeight: 700, lineHeight: 1, cursor: 'pointer', fontFamily: 'inherit' }}>{children}</button>
);
const ScenChip = ({ label, active, onClick }) => <button onClick={onClick} style={chipStyle(active, false)}>{label}</button>;
const chipStyle = (active, dashed) => ({
  fontSize: 11.5, padding: '5px 11px', borderRadius: 8,
  border: `1px ${dashed ? 'dashed' : 'solid'} ${active ? 'var(--cn-gold)' : 'rgba(168,144,96,0.2)'}`,
  background: active ? 'rgba(201,162,75,0.1)' : 'var(--cn-surface2)',
  color: dashed ? 'var(--cn-gold)' : active ? 'var(--cn-text)' : 'var(--cn-muted)',
  cursor: 'pointer', fontFamily: 'inherit',
});
const bannerStyle = { background: 'rgba(192,57,43,0.12)', border: '1px solid rgba(192,57,43,0.4)', borderRadius: 8, padding: '12px 15px', color: '#e8a89f', fontSize: 13, lineHeight: 1.5 };
// Flow-policy control styles.
const fpLabel = { fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--cn-dimmed)' };
const fpSelect = { fontSize: 12.5, padding: '6px 9px', borderRadius: 8, border: '1px solid rgba(168,144,96,0.24)', background: 'var(--cn-surface2)', color: 'var(--cn-text)', fontFamily: 'inherit' };
const fpInput = { width: 90, fontSize: 12.5, padding: '6px 9px', borderRadius: 8, border: '1px solid rgba(168,144,96,0.24)', background: 'var(--cn-surface2)', color: 'var(--cn-text)', fontFamily: 'inherit', textAlign: 'right', fontVariantNumeric: 'tabular-nums' };
const Pad = ({ children, muted, danger }) => (
  <div style={{ padding: '32px 36px', fontSize: 14, color: danger ? 'var(--cn-danger)' : muted ? 'var(--cn-muted)' : 'var(--cn-text)' }}>{children}</div>
);

function describeErr(e) {
  const s = e?.response?.status;
  if (s === 401) return 'This view needs MachineMetrics sign-in (401).';
  if (s === 403) return 'Access denied (403) — not the owner of this company.';
  return e?.response?.data?.error || e?.message || 'Request failed';
}
